import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Navigate, useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import api, { clearToken, getToken, onUnauthorized, setToken } from "api/client";
import { hasPermission } from "auth/access";

const AuthContext = createContext(null);
const RETRY_MS = 3000;

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(Boolean(getToken()));
  const [checkError, setCheckError] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    onUnauthorized(() => setAdmin(null));
    const check = () => {
      api
        .get("/api/admin/auth/me")
        .then((data) => {
          setAdmin(data);
          setCheckError(null);
          setChecking(false);
        })
        .catch((e) => {
          // 401 thì client.js đã xoá token. Còn token tức là GServer chưa chạy / đang khởi động lại:
          // giữ phiên và thử lại, không bắt đăng nhập lại.
          if (getToken()) {
            setCheckError(e.message);
            timer.current = setTimeout(check, RETRY_MS);
          } else {
            setChecking(false);
          }
        });
    };
    if (getToken()) {
      check();
    }
    return () => clearTimeout(timer.current);
  }, []);

  const value = useMemo(
    () => ({
      admin,
      checking,
      checkError,
      can: (permission) => hasPermission(admin, permission),
      /**
       * Trả null khi đã đăng nhập xong. Tài khoản bật xác thực 2 lớp thì trả { needTotp } (gọi lại kèm code),
       * chưa bật mà máy chủ bắt buộc thì trả { needTotpSetup, secret, uri } để quét QR rồi gọi lại kèm code.
       */
      login: async (username, password, remember = true, code = undefined) => {
        const data = await api.post("/api/admin/auth/login", {
          username,
          password,
          remember,
          code,
        });
        if (!data.token) {
          return data;
        }
        setToken(data.token, remember);
        setAdmin(data.admin);
        return null;
      },
      logout: () => {
        clearTimeout(timer.current);
        clearToken();
        setAdmin(null);
        setCheckError(null);
        setChecking(false);
      },
    }),
    [admin, checking, checkError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAuth() {
  return useContext(AuthContext);
}

/** Tài khoản đang đăng nhập có quyền này không (chủ luôn có). */
export function useCan(permission) {
  return useAuth().can(permission);
}

export function RequireAuth({ children }) {
  const { admin, checking, checkError, logout } = useAuth();
  const location = useLocation();

  if (checking) {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        px={3}
        textAlign="center"
      >
        <CircularProgress />
        {checkError && (
          <>
            <MDTypography variant="button" color="error" fontWeight="medium" mt={3}>
              {checkError}
            </MDTypography>
            <MDTypography variant="caption" color="text" mt={1}>
              Tự thử lại mỗi {RETRY_MS / 1000} giây. Kiểm tra cửa sổ &quot;Pet Realm GServer&quot;
              (chạy run-dev.bat).
            </MDTypography>
            <MDButton variant="outlined" color="info" size="small" sx={{ mt: 2 }} onClick={logout}>
              Về trang đăng nhập
            </MDButton>
          </>
        )}
      </MDBox>
    );
  }
  if (!admin) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

RequireAuth.propTypes = {
  children: PropTypes.node.isRequired,
};
